/**
 * @fileoverview Step Completion Hook - Computes step completion from runtime data.
 *
 * Aggregates data from multiple API queries (matter, police reports, providers,
 * coverages, blobs) to determine which steps have been completed. Used by the
 * sidebar stepper to show completion checkmarks.
 *
 * Completion is determined by either:
 * - Actual data exists (e.g., police report record exists)
 * - Section marked as reviewed in audit record (found or not_found)
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import { useGetBlobsQuery } from '@/services/api/blobService';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { useGetIncidentsQuery } from '@/services/api/incidentService';
import {
  useGetMatterCompanionsQuery,
  useGetMatterDetailsQuery,
} from '@/services/api/mattersService';
import { usePoliceReportsListQuery } from '@/services/api/policeReportsService';
import { useGetStaffNotesQuery } from '@/services/api/staffNotesService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';
import { CoverageCategory } from '@/services/types/coverage-types';
import type { MatterClient } from '@/services/types/matter-client-types';

import { useUnifiedFlow } from '../context/UnifiedFlowContext';
import {
  extractNoteContent,
  GENERAL_NOTES_HEADER,
  INTAKE_NOTES_HEADER,
} from '../utils/notesParser';

/**
 * Step completion status computed at runtime from actual data.
 */
export interface StepCompletionStatus {
  basics: boolean;
  case_info: boolean;
  client_info: boolean;
  companion_cases: boolean;
  liability: boolean;
  treatment: boolean;
  coverage: boolean;
  documents: boolean;
  case_summary: boolean;
  /** True while any of the underlying queries are still loading */
  isLoading: boolean;
}

/**
 * Check if all client fields are complete.
 * Required: first_name, last_name, email, phone, DOB, gender, SSN, language, address
 */
function isClientComplete(client: MatterClient | undefined): boolean {
  if (!client) return false;

  const hasName = !!client.first_name?.trim() && !!client.last_name?.trim();
  const hasEmail = !!client.email?.trim();
  const hasPhone = client.phone_numbers?.length > 0 && !!client.phone_numbers[0]?.phone_number;
  const hasDOB = !!client.date_of_birth;
  const hasGender = !!client.gender;
  const hasSSN = !!client.ssn || !!client.last_four_of_ssn;
  const hasLanguage = !!client.preferred_language;
  const hasAddress = !!(
    client.address?.line1 ||
    client.address?.city ||
    client.address?.state ||
    client.address?.postcode
  );

  return hasName && hasEmail && hasPhone && hasDOB && hasGender && hasSSN && hasLanguage && hasAddress;
}

/**
 * Hook that computes step completion status from runtime data.
 * Uses matter data, API queries, and audit record section statuses.
 */
export function useStepCompletion(matterId: string | null): StepCompletionStatus {
  const { getSectionStatuses } = useUnifiedFlow();
  const sectionStatuses = getSectionStatuses();

  // Fetch all needed data (skip if no matterId)
  const { data: matter, isLoading: matterLoading } = useGetMatterDetailsQuery(
    matterId ?? skipToken,
  );
  const { data: incidents, isLoading: incidentsLoading } = useGetIncidentsQuery(
    matterId ? { matterId } : skipToken,
  );
  const { data: policeReports, isLoading: policeLoading } = usePoliceReportsListQuery(
    matterId ? { matterId } : skipToken,
  );
  const { data: providers, isLoading: providersLoading } = useGetMedicalProvidersQuery(
    matterId ?? skipToken,
  );
  const { data: coverages, isLoading: coveragesLoading } = useGetCoveragesQuery(
    matterId ? { matterId } : skipToken,
  );
  const { data: blobs, isLoading: blobsLoading } = useGetBlobsQuery(matterId ?? skipToken);
  const { data: staffNotes, isLoading: staffNotesLoading } = useGetStaffNotesQuery(
    matterId ? { matterId } : skipToken,
  );
  const { data: companions, isLoading: companionsLoading } = useGetMatterCompanionsQuery(
    matterId ?? skipToken,
  );

  // Combined loading state - true until ALL queries complete
  const isLoading =
    matterLoading ||
    incidentsLoading ||
    policeLoading ||
    providersLoading ||
    coveragesLoading ||
    blobsLoading ||
    staffNotesLoading ||
    companionsLoading;

  const completionStatus = useMemo(() => {
    // Basics: matter exists
    const basics = !!matter;

    // Case Info: required fields present (incident location from incident API)
    const case_info = !!(
      matter?.date_of_loss &&
      matter?.statute_of_limitations_date &&
      incidents?.[0]?.location
    );

    // Client: all fields filled
    const client_info = isClientComplete(matter?.clients?.[0]);

    // Companion Cases: user is primary of a group OR is a member of a group (>1 members) OR section marked as reviewed
    const isPrimary = companions?.members.some((m) => m.id === matterId && m.is_primary) ?? false;
    const isGroupMember = (companions?.members.length ?? 0) > 1;
    const companion_cases = isPrimary || isGroupMember || sectionStatuses.companion_cases !== null;

    // Police: record exists OR section marked as reviewed (found or not_found)
    const hasPoliceReport = (policeReports?.length ?? 0) > 0;
    const liability = hasPoliceReport || sectionStatuses.police_report !== null;

    // Treatment: provider exists OR section marked as reviewed
    const hasProvider = (providers?.length ?? 0) > 0;
    const treatment = hasProvider || sectionStatuses.medical_providers !== null;

    // Coverage: each of 1P, 3P, Health must have data OR be marked as reviewed
    const has1P = coverages?.some((c) => c.category === CoverageCategory.FIRST_PARTY) ?? false;
    const has3P = coverages?.some((c) => c.category === CoverageCategory.THIRD_PARTY) ?? false;
    const hasHealth =
      coverages?.some((c) => c.category === CoverageCategory.FIRST_PARTY_HEALTH) ?? false;

    const coverage1pComplete = has1P || sectionStatuses.first_party_coverage !== null;
    const coverage3pComplete = has3P || sectionStatuses.third_party_coverage !== null;
    const coverageHealthComplete = hasHealth || sectionStatuses.health_insurance !== null;
    const coverage = coverage1pComplete && coverage3pComplete && coverageHealthComplete;

    // Documents: at least one uploaded
    const documents = (blobs?.length ?? 0) > 0;

    // Notes: complete if either intake or general notes has content
    const intakeNote = staffNotes?.find((n) => n.content.startsWith(INTAKE_NOTES_HEADER));
    const generalNote = staffNotes?.find((n) => n.content.startsWith(GENERAL_NOTES_HEADER));
    const hasIntakeContent = !!extractNoteContent(intakeNote?.content || '', INTAKE_NOTES_HEADER).trim();
    const hasGeneralContent = !!extractNoteContent(generalNote?.content || '', GENERAL_NOTES_HEADER).trim();
    const case_summary = hasIntakeContent || hasGeneralContent;

    return {
      basics,
      case_info,
      client_info,
      companion_cases,
      liability,
      treatment,
      coverage,
      documents,
      case_summary,
    };
  }, [matter, incidents, policeReports, providers, coverages, blobs, staffNotes, companions, matterId, sectionStatuses]);

  return {
    ...completionStatus,
    isLoading,
  };
}
